/**
 * Share dialog — generates / displays / revokes the public read-only link
 * for the current project. Owned by ProjectBuilder.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, ArrowSquareOut, Trash, Share } from "@phosphor-icons/react";

export default function ShareDialog({ open, onClose, projectId }) {
  const [info, setInfo]   = useState(null);   // { token, created_at, view_count } | null
  const [busy, setBusy]   = useState(false);
  const [loaded, setLoaded] = useState(false);
  const urlInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/share`);
        setInfo(data?.token ? data : null);
      } catch {
        setInfo(null);
      } finally { setLoaded(true); }
    })();
  }, [open, projectId]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/share`);
      setInfo(data);
      toast.success("Share link created");
    } catch {
      toast.error("Couldn't create share link");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this link? Anyone with the URL will lose access.")) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/share`);
      setInfo(null);
      toast.success("Share link revoked");
    } catch {
      toast.error("Couldn't revoke share link");
    } finally { setBusy(false); }
  };

  const url = info ? `${window.location.origin}/share/${info.token}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      // Clipboard API unavailable (insecure context / denied) — select the
      // input text so the user can hit Ctrl+C immediately.
      urlInputRef.current?.select();
      toast.info("Press Ctrl+C — copy not available in this context");
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="share-dialog-backdrop">
      <div
        className="bg-[#0d1015] border border-white/10 w-full max-w-xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-dialog"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-tag">// share</div>
            <h2 className="font-display text-2xl font-bold inline-flex items-center gap-2">
              <Share size={22} /> Share read-only project
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Anyone with the link can view specs, equipment list, BOM, cable schedule and download the PDF — without an account.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="share-dialog-close">
            <X size={20} />
          </button>
        </div>

        {!loaded ? (
          <div className="text-gray-500 font-mono text-sm p-6">Loading…</div>
        ) : info ? (
          <>
            <div className="label-tag mb-1">Public URL</div>
            <div className="flex gap-2 mb-4">
              <input
                ref={urlInputRef}
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                className="input-base text-sm flex-1 font-mono"
                data-testid="share-url-input"
              />
              <button onClick={copy} className="btn-primary text-sm py-2 inline-flex items-center gap-1" data-testid="share-copy-btn">
                <Copy size={14} /> Copy
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4 text-xs font-mono">
              <Stat label="Created" value={info.created_at ? new Date(info.created_at).toLocaleString() : "—"} />
              <Stat label="Views" value={info.view_count ?? 0} testid="share-views" />
              <Stat label="Status" value="Active" valueClass="text-[#22c55e]" />
            </div>

            <div className="flex gap-2 flex-wrap">
              <a href={url} target="_blank" rel="noreferrer"
                 className="btn-ghost text-sm py-2 inline-flex items-center gap-2"
                 data-testid="share-open-preview">
                <ArrowSquareOut size={14} /> Open preview
              </a>
              <button onClick={revoke} disabled={busy}
                      className="btn-ghost text-sm py-2 inline-flex items-center gap-2 text-red-400 hover:text-red-300"
                      data-testid="share-revoke-btn">
                <Trash size={14} /> Revoke link
              </button>
            </div>
          </>
        ) : (
          <div>
            <div className="border border-yellow-500/30 bg-yellow-500/5 p-3 text-xs text-yellow-200 font-mono mb-4">
              No public link yet. Generate one to share this design with a client, inspector or teammate.
            </div>
            <button onClick={generate} disabled={busy}
                    className="btn-primary text-sm py-2 inline-flex items-center gap-2"
                    data-testid="share-generate-btn">
              <Share size={14} weight="bold" /> {busy ? "Creating…" : "Create share link"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, valueClass = "text-[#00E5FF]" }) {
  return (
    <div className="bg-[#0a0d12] border border-white/10 p-3" data-testid={testid}>
      <div className="label-tag text-[10px]">{label}</div>
      <div className={`font-display text-base font-bold ${valueClass}`}>{value}</div>
    </div>
  );
}
